import { Injectable, effect, inject } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { IdleTimer } from 'shared-resources';

import { OasisAuthService } from './oasis-auth.service';
import { SessionIdleComponent, SessionIdleResult } from './session-idle.component';

/** Inactivity before the warning appears. Matches the internal app. */
const IDLE_MS = 25 * 60 * 1000;

/** How long the warning counts down before signing out. Matches the internal app. */
const WARNING_MS = 5 * 60 * 1000;

/** How often the phase is re-derived. One second, because the countdown is per-second. */
const TICK_MS = 1000;

/**
 * Shared across every tab on this origin, which is what makes the idle clock
 * cross-tab. Internal name on purpose — a storage key is not user-visible.
 */
const ACTIVITY_KEY = 'oasis.session.lastActivityAt';

/** Coarse enough that a moving mouse does not write to storage on every frame. */
const ACTIVITY_THROTTLE_MS = 1000;

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel'] as const;

/**
 * Signs an idle applicant out, with a counted-down warning first.
 *
 * Why this exists: the Cognito refresh token bounds a session at 1 day (see
 * `OasisExternalUserPoolClient` in `template-persistent.yaml`), and when it lapsed the
 * applicant found out at their next "Save & Continue" — a failed save reported as a
 * connection problem, with nothing to suggest signing in again was the fix. An idle
 * timeout makes the common case an explained sign-out instead, and at 30 minutes total
 * it is the binding constraint long before the token clock is.
 *
 * Deliberately does NOT draft-save before signing out. Save intervals are already per
 * screen so an interrupted applicant loses little, and writing the in-progress body
 * somewhere durable to survive a sign-out would put application PII into browser
 * storage to solve a problem the save cadence already bounds.
 *
 * Timing lives in `IdleTimer` (dependency-free, in `shared-resources`, unit-tested
 * there). This class owns only the impure parts: the clock, the interval, the DOM
 * listeners, storage, the modal and the auth service.
 *
 * **Does not use `@ng-idle/core`, and must not.** That package references the global
 * `Zone` directly, so it throws `ReferenceError: Zone is not defined` in this app —
 * the applicant portal has no `polyfills` entry in `angular.json` and therefore runs
 * zoneless. Its last release was Sept 2024 and upstream is unmaintained, so there is
 * no version that fixes this.
 */
@Injectable({ providedIn: 'root' })
export class SessionIdleService {
	private readonly modal = inject(NgbModal);
	private readonly auth = inject(OasisAuthService);

	private readonly timer = new IdleTimer({ idleMs: IDLE_MS, warningMs: WARNING_MS });

	/** Set only while the warning is open; `undefined` at every other time. */
	private ref?: NgbModalRef;

	private intervalId?: ReturnType<typeof setInterval>;
	private lastWrittenAt = 0;

	/** Bound once so add/removeEventListener see the same reference. */
	private readonly onActivity = (): void => this.recordActivity();

	constructor() {
		// Only count inactivity while signed in. Watching before sign-in would pop a
		// session warning at an anonymous visitor on the landing page and then "sign
		// out" someone who was never signed in.
		effect(() => {
			if (this.auth.isAuthenticated()) {
				this.start();
			} else {
				this.stop();
			}
		});
	}

	private start(): void {
		if (this.intervalId !== undefined) {
			return;
		}
		// Seed the clock so a fresh sign-in does not inherit a stale timestamp from a
		// previous session in this browser.
		this.writeLastActivity(Date.now());
		for (const event of ACTIVITY_EVENTS) {
			window.addEventListener(event, this.onActivity, { passive: true });
		}
		this.intervalId = setInterval(() => this.tick(), TICK_MS);
	}

	private stop(): void {
		for (const event of ACTIVITY_EVENTS) {
			window.removeEventListener(event, this.onActivity);
		}
		if (this.intervalId !== undefined) {
			clearInterval(this.intervalId);
			this.intervalId = undefined;
		}
		this.closeWarning();
	}

	/**
	 * Re-derive the phase from the clock and act on it.
	 *
	 * Derived rather than accumulated, so a machine that slept through the warning
	 * signs out on the first tick after waking instead of resuming a countdown that
	 * already ran out.
	 */
	private tick(): void {
		const decision = this.timer.decide(this.readLastActivity(), Date.now());

		switch (decision.phase) {
			case 'active':
				// Reached when another tab records activity, or when this tab's own
				// warning was dismissed. Either way the warning should not be up.
				this.closeWarning();
				return;

			case 'warning':
				this.showWarning();
				(this.ref?.componentInstance as SessionIdleComponent | undefined)?.setCountdown(decision.secondsRemaining);
				return;

			case 'expired':
				this.signOut();
				return;
		}
	}

	/**
	 * Record activity, throttled.
	 *
	 * Ignored while the warning is up, on purpose: the warning is an explicit decision
	 * point, and the mouse movement needed to reach either button would otherwise
	 * dismiss it before the click landed. That was a real bug in the internal app's
	 * original implementation, where both buttons had been commented out because they
	 * were unreachable for exactly this reason.
	 */
	private recordActivity(): void {
		if (this.ref) {
			return;
		}
		const now = Date.now();
		if (now - this.lastWrittenAt < ACTIVITY_THROTTLE_MS) {
			return;
		}
		this.writeLastActivity(now);
	}

	private showWarning(): void {
		if (this.ref) {
			return;
		}
		const ref = this.modal.open(SessionIdleComponent, {
			centered: true,
			// A stray click or Esc should not resolve a security decision by accident,
			// and this leaves no dismissal path to reject `result`.
			backdrop: 'static',
			keyboard: false,
			ariaLabelledBy: 'session-idle-title',
		});
		this.ref = ref;
		ref.result.then(
			(result: SessionIdleResult) => (result === 'Reset' ? this.dismissWarning() : this.signOut()),
			// Defensive only: with a static backdrop and no keyboard dismiss there is no
			// path here. Treat an unexpected one as "keep working" rather than signing
			// someone out for a reason we cannot explain.
			() => this.dismissWarning(),
		);
	}

	/** "I'm still here": clear the warning and restart the clock, in every tab. */
	private dismissWarning(): void {
		this.closeWarning();
		this.writeLastActivity(Date.now());
	}

	private closeWarning(): void {
		const ref = this.ref;
		if (!ref) {
			return;
		}
		// Cleared first so the result handler sees no open warning and cannot recurse.
		this.ref = undefined;
		ref.close('Reset');
	}

	/** End the session: stop watching, then redirect through Cognito logout. */
	private signOut(): void {
		this.stop();
		this.auth.logout();
	}

	private readLastActivity(): number {
		const raw = window.localStorage.getItem(ACTIVITY_KEY);
		const parsed = raw === null ? Number.NaN : Number(raw);
		// A missing or corrupt value must not read as "idle since 1970" and sign the
		// applicant out instantly, so treat it as activity now and re-seed.
		if (!Number.isFinite(parsed)) {
			const now = Date.now();
			this.writeLastActivity(now);
			return now;
		}
		return parsed;
	}

	private writeLastActivity(atMs: number): void {
		this.lastWrittenAt = atMs;
		window.localStorage.setItem(ACTIVITY_KEY, String(atMs));
	}
}
